/**
 * Cabling.
 *
 * Six slots: a power cord for each powered component, and one each of the four
 * system cables.
 *
 * Every slot shows what the engine ACTUALLY DID with that cable, read from the
 * compiled system rather than asserted here. For the two types with a mechanism
 * that is a computed deviation in dB; for the other three it is an explicit
 * statement that nothing was applied, with the reason one click away. A slot
 * that silently did nothing while looking like it did something would be the
 * worst possible outcome for this feature.
 */

import { useState } from 'react';
import { CABLE_TYPE_RATIONALE, cableAffectsSignalPath } from '../../types/hardware';
import type { CableSlotId } from '../../types/system';
import { CABLE_SLOT_LABEL, CABLE_SLOT_TYPE } from '../../types/system';
import { useApp } from '../../stores/appStore';
import { Note, SectionTitle, fmtPrice } from './primitives';

const SYSTEM_SLOTS: CableSlotId[] = ['interconnect', 'usb', 'ethernet', 'speakerCable'];
const POWER_SLOTS: CableSlotId[] = ['dacPower', 'ampPower'];

export function CablePanel() {
  return (
    <div className="panel-section">
      <SectionTitle>Cabling</SectionTitle>
      <div className="tiny faint" style={{ margin: '0 0 6px' }}>
        Power cords
      </div>
      {POWER_SLOTS.map((s) => (
        <CableSlot key={s} slot={s} />
      ))}
      <div className="tiny faint" style={{ margin: '8px 0 6px' }}>
        System cables
      </div>
      {SYSTEM_SLOTS.map((s) => (
        <CableSlot key={s} slot={s} />
      ))}
    </div>
  );
}

export function CableSlot({ slot }: { slot: CableSlotId }) {
  const activeSystem = useApp((s) => s.activeSystem);
  const selection = useApp((s) => (activeSystem === 'A' ? s.project.systemA : s.project.systemB).cables[slot]);
  const cable = useApp((s) => s.cableFor(activeSystem, slot));
  const report = useApp((s) => s.compiled[activeSystem]?.cables?.[slot]);
  const openLibrary = useApp((s) => s.openLibrary);
  const setCableLength = useApp((s) => s.setCableLength);
  const blind = useApp((s) => s.blind);
  const [showWhy, setShowWhy] = useState(false);

  const type = CABLE_SLOT_TYPE[slot];
  // The compiled report wins over the cable's type: a loudspeaker cable with a
  // headphone selected is a modelled TYPE that is nonetheless not in the
  // circuit, and reportCable() is the thing that knows the difference.
  const modelled = report ? report.modelled : cableAffectsSignalPath(type);

  return (
    <div style={{ marginBottom: 7, paddingBottom: 6, borderBottom: '1px solid #14181e' }}>
      <button
        className="btn wide"
        style={{ justifyContent: 'flex-start', height: 34, textAlign: 'left' }}
        onClick={() => openLibrary(slot, activeSystem)}
      >
        <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0, flex: 1 }}>
          <span className="tiny faint" style={{ letterSpacing: '0.09em' }}>
            {CABLE_SLOT_LABEL[slot]}
          </span>
          <span
            className={cable ? 'bright' : 'faint'}
            style={{ fontSize: 11.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}
          >
            {blind ? '••••••' : cable ? `${cable.manufacturer} ${cable.model}` : 'None'}
          </span>
        </span>
        <span className="faint">▸</span>
      </button>

      {cable && (
        <>
          <div className="row" style={{ marginTop: 4, gap: 6 }}>
            <span className="tiny faint" style={{ width: 44 }}>
              Length
            </span>
            <input
              type="range"
              min={0.1}
              max={slot === 'speakerCable' ? 15 : 8}
              step={0.1}
              value={selection.lengthM}
              onChange={(e) => setCableLength(activeSystem, slot, Number(e.target.value))}
              style={{ flex: 1 }}
              aria-label={`${CABLE_SLOT_LABEL[slot]} length`}
            />
            <input
              type="number"
              min={0.1}
              max={30}
              step={0.05}
              value={selection.lengthM}
              onChange={(e) => setCableLength(activeSystem, slot, Number(e.target.value))}
              style={{ width: 62 }}
              aria-label={`${CABLE_SLOT_LABEL[slot]} length in metres`}
            />
            <span className="tiny faint">m</span>
          </div>

          <div className="row tiny" style={{ marginTop: 3, gap: 8 }}>
            {!blind && <span className="faint">{fmtPrice(cable.price)}{cable.priceLengthM ? ` / ${cable.priceLengthM} m` : ''}</span>}
            {modelled ? (
              <span
                className={report && report.maxDeviationDb >= 0.05 ? 'warnc' : 'okc'}
                title={report?.summary}
              >
                {report ? `${report.maxDeviationDb < 0.005 ? '<0.01' : report.maxDeviationDb.toFixed(2)} dB` : '--'}
              </span>
            ) : (
              <span className="dim">no signal effect</span>
            )}
            <button
              className="fav"
              style={{ marginLeft: 'auto', fontSize: 10 }}
              onClick={() => setShowWhy((v) => !v)}
              title="Why?"
            >
              {showWhy ? 'hide' : 'why?'}
            </button>
          </div>

          {showWhy && (
            <div style={{ marginTop: 5 }}>
              <Note tone={modelled ? undefined : 'info'}>
                {CABLE_TYPE_RATIONALE[type]}
                {report?.summary ? ` — ${report.summary}` : ''}
              </Note>
            </div>
          )}
        </>
      )}
    </div>
  );
}
